import React, { useEffect, useContext, useState, useCallback } from 'react'
import useSWR from 'swr'
import { fetchPaymentDetails, UserID } from '@library/api-payment'
import {
  Accordion,
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  FlexDirection,
  FontWeight,
  FormFieldStatus,
  InputField,
  JustifyContent,
  Space,
  Spinner,
  Text,
  useModal,
} from '@gousto-internal/citrus-react'
import { AccountDetailsContext } from '../../../AccountDetailsContext'
import { RenderedTitle } from '../../RenderedTitle'
import { ActionType } from '../../../interfaces'
import { useCheckoutComFrame } from '../hooks/useCheckoutComFrame'
import { usePaymentCard3DSModal } from '../hooks/usePaymentCard3DSModal'
import { Title, DataFrame } from '../../../enums'
import { Form, InputFrame } from './Form'
import { Notification } from '../../Notification'

type Props = {
  userId: UserID
}

// Invalidate special characters
const invalidNameOnCardPattern = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/

const renderTitle = (isExpanded: boolean, title: string, content: string) => {
  return (
    <RenderedTitle isExpanded={isExpanded} title={title} iconType="credit_card">
      <Text size={2}>{content}</Text>
    </RenderedTitle>
  )
}

const initialState = {
  inputNameOnCard: undefined,
  inputNameOnCardError: false,
  accordion: 'expanded',
}

export const PaymentCardSection = (props: Props) => {
  const { data } = useSWR(props.userId, fetchPaymentDetails)
  const { openModal, closeCurrentModal } = useModal()

  const { state, dispatch } = useContext(AccountDetailsContext)

  const [key, updateKey] = useState(Date.now())
  const [inputNameOnCard, setInputNameOnCard] = useState<string | undefined>(
    initialState.inputNameOnCard,
  )
  const [inputNameOnCardError, setInputNameOnCardError] = useState<boolean>(false)

  const {
    cardConfirmationRedirectURL,
    error: checkoutComError,
    formValidationError,
    hasCheckoutFrameStarted,
    isCheckoutFormValid,
    isCheckoutFormSubmitting,
    onSubmitCheckout,
    reset: resetCheckout,
    startCheckoutCom,
  } = useCheckoutComFrame(props.userId)

  const {
    cardConfirmationError,
    cardConfirmationCompleted,
    setCardConfirmationRedirectURL,
    reset: reset3DS,
  } = usePaymentCard3DSModal()

  const resetState = useCallback(() => {
    reset3DS()
    resetCheckout()

    setInputNameOnCard('')
    setInputNameOnCardError(false)
  }, [reset3DS, resetCheckout, setInputNameOnCard, setInputNameOnCardError])

  const renderAccordionHeader = (isExpanded: boolean): JSX.Element => {
    switch (data?.method) {
      case 'card':
        const paymentCardNumber = data?.details.number
        return renderTitle(isExpanded, Title.CARD, paymentCardNumber)
      case 'paypal':
        return renderTitle(isExpanded, Title.PAYPAL, 'PayPal')
      case 'applepay':
        return renderTitle(isExpanded, Title.APPLE_PAY, 'Apple Pay')
      default:
        return renderTitle(isExpanded, Title.CARD, 'No payment data found')
    }
  }

  const onChangeCallback = useCallback(
    (isExpanded) => {
      dispatch({
        type: ActionType.RESET_CLICKED_STATE,
      })

      if (!isExpanded) {
        resetState()
      }
    },
    [dispatch, resetState],
  )

  useEffect(() => {
    if (!state.accountOnHoldLinkClicked) {
      return
    }
    updateKey(Date.now())
    startCheckoutCom()
  }, [state.accountOnHoldLinkClicked, startCheckoutCom])

  useEffect(() => {
    if (cardConfirmationRedirectURL) {
      setCardConfirmationRedirectURL(cardConfirmationRedirectURL)

      openModal('PaymentCard3DSModal')
    }
  }, [setCardConfirmationRedirectURL, openModal, cardConfirmationRedirectURL])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    if (cardConfirmationCompleted) {
      // Before closing the modal, it will give it a few seconds
      // to the costumer to read successful/failure message from the modal
      timeout = setTimeout(() => {
        closeCurrentModal()
      }, 2000)
    }

    return function cleanup() {
      clearTimeout(timeout)
    }
  }, [closeCurrentModal, cardConfirmationCompleted, resetState])

  const handleNameOnCard = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement
      const invalidField =
        target.value.match(invalidNameOnCardPattern) !== null || target.value.length < 1

      setInputNameOnCardError(invalidField)
      setInputNameOnCard(target.value)
    },
    [setInputNameOnCard, setInputNameOnCardError],
  )

  const handleSubmit = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault()

      onSubmitCheckout(inputNameOnCard)
    },
    [onSubmitCheckout, inputNameOnCard],
  )

  return (
    <>
      <Accordion
        id={'payment-details-accordion'}
        key={`payment-card-key-${key}`}
        onChange={onChangeCallback}
        initialState={state.accountOnHoldLinkClicked ? 'expanded' : undefined}
        title={renderAccordionHeader}
      >
        {!hasCheckoutFrameStarted && (
          <Box
            data-testid="loadingCardPayment"
            display="flex"
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
          >
            <Spinner minWidth="3rem" minHeight="3rem" />
          </Box>
        )}
        {/* Form has to be rendered so checkout.com Frames can read input data-frames when it starts*/}
        <Form
          data-testid="formCardPayment"
          onSubmit={handleSubmit}
          className={hasCheckoutFrameStarted ? 'form--activated' : undefined}
        >
          {cardConfirmationError && <Notification state="error" message={cardConfirmationError} />}
          {!isCheckoutFormSubmitting && checkoutComError && (
            <Notification state="error" message={checkoutComError} />
          )}
          {!cardConfirmationError && cardConfirmationCompleted && (
            <Notification state="success" message="Card updated successfully" />
          )}
          <Box paddingBottom={2}>
            {/* Name on card is not an input required nor provided by checkout.com */}
            <InputField
              id="nameOnCard"
              label="Name on card"
              required
              value={inputNameOnCard}
              validationMessage={inputNameOnCardError && 'Please enter a valid name on card'}
              status={inputNameOnCardError ? FormFieldStatus.Error : undefined}
              onChange={handleNameOnCard}
            />
          </Box>
          <Box paddingV={1}>
            <Text fontWeight={FontWeight.SemiBold}>Card number</Text>
            <Space direction="vertical" size={2} />
            <InputFrame data-frames={DataFrame.CARD_NUMBER} />
            {formValidationError?.cardNumber && (
              <Text size={1} color={Color.Error_900}>
                {formValidationError.cardNumber}
              </Text>
            )}
          </Box>
          <Box paddingV={2} display="flex" flexDirection={FlexDirection.Row}>
            <Box paddingRight={2}>
              <Text fontWeight={FontWeight.SemiBold}>Expiry date</Text>
              <Space direction="vertical" size={2} />
              <InputFrame data-frames={DataFrame.EXPIRY_DATE} />
              {formValidationError?.expiryDate && (
                <Text size={1} color={Color.Error_900}>
                  {formValidationError.expiryDate}
                </Text>
              )}
            </Box>
            <Box>
              <Text fontWeight={FontWeight.SemiBold}>CVV</Text>
              <Space direction="vertical" size={2} />
              <InputFrame data-frames={DataFrame.CVV} />
              {formValidationError?.cvv && (
                <Text size={1} color={Color.Error_900}>
                  {formValidationError.cvv}
                </Text>
              )}
            </Box>
          </Box>
          <Space direction="vertical" size={2} />
          <Button
            colorVariant={ButtonColorVariant.Primary}
            width="100%"
            type="submit"
            size={ButtonSize.Medium}
            disabled={
              !inputNameOnCard ||
              !isCheckoutFormValid ||
              inputNameOnCardError ||
              isCheckoutFormSubmitting
            }
          >
            Save changes
          </Button>
        </Form>
      </Accordion>
    </>
  )
}
